import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { Alert, KeyboardAvoidingView, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Surface, TextInput } from 'react-native-paper';

const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 500;

const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  green600: '#16A34A',
};

export default function ResetPasswordScreen() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // ✅ Détecter la session de recovery créée par le lien email
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        setSessionReady(true);
      }
      setCheckingSession(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
        setCheckingSession(false);
      }
    });

    checkSession();

    return () => subscription.unsubscribe();
  }, []);

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      Alert.alert('Erreur', 'Remplis tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert('Erreur', 'Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      Alert.alert('Erreur', 'Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setLoading(false);

    if (error) {
      Alert.alert('Erreur', error.message);
    } else {
      setSuccess(true);
    }
  };

  // ✅ Écran de succès
  if (success) {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <Surface style={styles.formCard} elevation={1}>
            <View style={styles.successContainer}>
              <Text style={styles.successEmoji}>✅</Text>
              <Text style={styles.successTitle}>Mot de passe modifié !</Text>
              <Text style={styles.successText}>
                Ton nouveau mot de passe est enregistré. Tu peux maintenant te connecter avec.
              </Text>
              <TouchableOpacity
                style={styles.button}
                onPress={() => router.replace('/login')}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.buttonGradient}
                >
                  <Text style={styles.buttonText}>Se connecter</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </Surface>
        </View>
      </View>
    );
  }

  // ✅ Écran de chargement / vérification session
  if (checkingSession) {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <ActivityIndicator size="large" color={colors.indigo600} />
          <Text style={[styles.subtitle, { marginTop: 16, textAlign: 'center' }]}>
            Vérification en cours...
          </Text>
        </View>
      </View>
    );
  }

  // ✅ Pas de session recovery → lien invalide ou expiré
  if (!sessionReady) {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <Surface style={styles.formCard} elevation={1}>
            <View style={styles.successContainer}>
              <Text style={styles.successEmoji}>⏰</Text>
              <Text style={styles.successTitle}>Lien expiré</Text>
              <Text style={styles.successText}>
                Ce lien de réinitialisation n'est plus valide. Retourne sur l'app et clique sur "Mot de passe oublié" pour en recevoir un nouveau.
              </Text>
              <TouchableOpacity
                style={styles.button}
                onPress={() => router.replace('/login')}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.buttonGradient}
                >
                  <Text style={styles.buttonText}>Retour à la connexion</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </Surface>
        </View>
      </View>
    );
  }

  // ✅ Formulaire de nouveau mot de passe
  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.emoji}>🔑</Text>
          <Text style={styles.title}>Nouveau mot de passe</Text>
          <Text style={styles.subtitle}>
            Choisis un nouveau mot de passe pour ton compte Ernest
          </Text>
        </View>

        <Surface style={styles.formCard} elevation={1}>
          <View style={styles.form}>
            <TextInput
              label="Nouveau mot de passe"
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry={!showPassword}
              mode="outlined"
              style={styles.input}
              outlineColor={colors.slate200}
              activeOutlineColor={colors.indigo600}
              textColor={colors.slate900}
              right={
                <TextInput.Icon
                  icon={showPassword ? 'eye-off' : 'eye'}
                  onPress={() => setShowPassword(!showPassword)}
                  color={colors.slate400}
                />
              }
            />

            <TextInput
              label="Confirmer le mot de passe"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!showConfirmPassword}
              mode="outlined"
              style={styles.input}
              outlineColor={colors.slate200}
              activeOutlineColor={colors.indigo600}
              textColor={colors.slate900}
              right={
                <TextInput.Icon
                  icon={showConfirmPassword ? 'eye-off' : 'eye'}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  color={colors.slate400}
                />
              }
            />

            <TouchableOpacity
              style={[styles.button, loading && styles.buttonDisabled]}
              onPress={handleResetPassword}
              disabled={loading}
              activeOpacity={0.8}
            >
              <LinearGradient
                colors={[colors.indigo600, colors.purple600]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.buttonGradient}
              >
                {loading ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.buttonText}>Enregistrer</Text>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </Surface>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  content: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  emoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: 20,
  },
  formCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 24,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 16,
    elevation: 4,
  },
  form: {
    gap: 14,
  },
  input: {
    backgroundColor: colors.white,
  },
  button: {
    marginTop: 8,
    borderRadius: 14,
    overflow: 'hidden',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 4,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonGradient: {
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },
  successContainer: {
    alignItems: 'center',
    padding: 8,
  },
  successEmoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
    textAlign: 'center',
  },
  successText: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
});